import {
	actionEnableGodMode,
	checkGeo,
	checkGeoSuccess,
	checkTerms,
	checkTermsSuccess,
	checkUserName,
	checkUserNameFailed,
	checkUserNameSuccess,
	fetchFavoritePlayers,
	fetchFavoritePlayersSuccess,
	fetchFavoriteSquads,
	fetchFavoriteSquadsSuccess,
	fetchUserRewardsSuccess,
	getCountrySuccess,
	postLoginViaBackDoor,
	postLoginViaBackDoorError,
	postUserLogoutSuccess,
	retrieveUserSuccess,
	setForceTutorialModal,
	setSessionState,
	setViewRewardModalSuccess,
} from "modules/actions/user";
import {IS_SECRET} from "modules/constants";
import {RequestState} from "modules/enums";
import {createReducer} from "redux-act";

interface IUserStore {
	loadingState: RequestState;
	sessionState: RequestState;
	geoRequestState: RequestState;
	favoriteRequestState: RequestState;
	checkUserNameRequestState: RequestState;
	checkTermsRequestState: RequestState;
	currentUser: ReturnType<typeof retrieveUserSuccess>["payload"]["user"] | null;
	terms: boolean;
	userHasUserName: boolean;
	isCountryAllowed: boolean;
	country: ReturnType<typeof getCountrySuccess>["payload"];
	favoritePlayers: ReturnType<typeof fetchFavoritePlayersSuccess>["payload"];
	favoriteSquads: ReturnType<typeof fetchFavoriteSquadsSuccess>["payload"];
	isSecretEnabled: boolean;
	godMode: boolean;
	stateCode: string | null;
	countryCode: string | null;
	rewards: string[];
}

const initialState: IUserStore = {
	loadingState: RequestState.IDLE,
	checkTermsRequestState: RequestState.IDLE,
	sessionState: RequestState.IDLE,
	geoRequestState: RequestState.IDLE,
	favoriteRequestState: RequestState.IDLE,
	checkUserNameRequestState: RequestState.IDLE,
	currentUser: null,
	terms: false,
	userHasUserName: false,
	isCountryAllowed: false,
	favoritePlayers: {
		favoritePlayerIds: [],
		mostSelectedPlayerIds: [],
	},
	favoriteSquads: {
		favoriteSquadIds: [],
	},
	country: {
		isoCode: "",
		isoStateCode: "",
	},
	isSecretEnabled: IS_SECRET,
	godMode: false,
	stateCode: null,
	countryCode: null,
	rewards: [],
};

export const user = createReducer({}, initialState);

/**
 * Backdoor user flow;
 */
user.on(postLoginViaBackDoor, (state) => ({
	...state,
	loadingState: RequestState.Requested,
}))
	.on(retrieveUserSuccess, (state, payload) => ({
		...state,
		currentUser: payload.user,
		loadingState: RequestState.Received,
		terms: true,
		countryCode: state.countryCode || payload.user.countryCode,
		stateCode: state.stateCode || payload.user.stateCode,
	}))
	.on(setSessionState, (state, payload) => ({
		...state,
		sessionState: payload,
	}))
	.on(postUserLogoutSuccess, () => ({
		...initialState,
		currentUser: null,
	}))
	.on(postLoginViaBackDoorError, (state) => ({
		...state,
		loadingState: RequestState.Received,
	}))
	.on(checkGeo, (state) => ({
		...state,
		geoRequestState: RequestState.Requested,
	}))
	.on(getCountrySuccess, (state, payload) => ({
		...state,
		country: payload,
	}))
	.on(checkGeoSuccess, (state, payload) => ({
		...state,
		geoRequestState: RequestState.Received,
		isCountryAllowed: payload.isAllowed,
	}))
	.on(checkTerms, (state) => ({
		...state,
		checkTermsRequestState: RequestState.Requested,
	}))
	.on(checkTermsSuccess, (state, payload) => ({
		...state,
		checkTermsRequestState: RequestState.Received,
		terms: payload.user.terms,
	}))
	.on(checkUserNameFailed, (state) => ({
		...state,
		checkUserNameRequestState: RequestState.Received,
	}))
	.on(checkUserName, (state) => ({
		...state,
		checkUserNameRequestState: RequestState.Requested,
	}))
	.on(checkUserNameSuccess, (state, payload) => ({
		...state,
		checkUserNameRequestState: RequestState.Received,
		userHasUserName: payload.user.username,
		stateCode: payload.user.stateCode,
		countryCode: payload.user.countryCode,
	}))
	.on(checkUserNameFailed, (state) => ({
		...state,
		checkUserNameRequestState: RequestState.Received,
	}))
	.on(fetchFavoritePlayers, (state) => ({
		...state,
		favoriteRequestState: RequestState.Requested,
	}))
	.on(fetchFavoriteSquads, (state) => ({
		...state,
		favoriteRequestState: RequestState.Requested,
	}))
	.on(fetchFavoritePlayersSuccess, (state, payload) => ({
		...state,
		favoritePlayers: payload,
		favoriteRequestState: RequestState.Received,
	}))
	.on(fetchFavoriteSquadsSuccess, (state, payload) => ({
		...state,
		favoriteSquads: payload,
		favoriteRequestState: RequestState.Received,
	}))
	.on(setForceTutorialModal, (state, payload) => ({
		...state,
		currentUser: state.currentUser
			? {
					...state.currentUser,
					isTutorialOpenForced: payload,
			  }
			: null,
	}))
	.on(actionEnableGodMode, (state) => ({
		...state,
		godMode: true,
	}))
	.on(setViewRewardModalSuccess, (state, payload) => ({
		...state,
		currentUser: payload.user,
	}))
	.on(fetchUserRewardsSuccess, (state, {rewards}) => ({
		...state,
		rewards,
	}));
